/**
 * The equipment type behind a pricebook EQUIPMENT item.
 *
 * The storage split is real — the make/model spec lives on an
 * EquipmentDescription and the price on the pricebook item — but an operator
 * adding a condenser is describing one thing. So both pricebook forms render
 * the spec panels and both write the type through here, rather than sending the
 * operator to a second screen to build the same equipment again.
 */
import type { EquipmentDescription } from '@/types/equipment-description';

/** The equipment type as a pricebook form describes it, ready to POST or PUT. */
export type EquipmentTypeSpec = Record<string, unknown>;

/** The subset of a pricebook item form the equipment panels bind to. */
export interface EquipmentTypeFormFields {
  name: string;
  code: string;
  description?: string;
  equipmentCategoryKey?: string;
  brand?: string;
  equipmentModel?: string;
  manufacturer?: string;
  mpn?: string;
  upc?: string;
  lengthIn?: number | null;
  widthIn?: number | null;
  heightIn?: number | null;
  weightLbs?: number | null;
  capacity?: string;
  fuelType?: string;
  efficiencyRating?: string;
  refrigerantType?: string;
  filterSize?: string;
  expectedLifespanYears?: number | null;
  defaultManufacturerWarrantyMonths?: number | null;
  defaultManufacturerWarrantyTerms?: string;
  defaultServiceProviderWarrantyMonths?: number | null;
  defaultServiceProviderWarrantyTerms?: string;
}

/**
 * The panel fields by name, in one fixed order.
 *
 * The sidebar checklist has to ask `hasEquipmentSpecContent` about LIVE values
 * — the requirement it renders turns on them — and watching the whole form to
 * do that would put every keystroke on a very large page's render path. So a
 * form subscribes to exactly these and hands the watched tuple back through
 * `equipmentSpecFieldsFromWatched`.
 */
export const EQUIPMENT_SPEC_FIELD_NAMES = [
  'equipmentCategoryKey',
  'brand',
  'equipmentModel',
  'manufacturer',
  'mpn',
  'upc',
  'lengthIn',
  'widthIn',
  'heightIn',
  'weightLbs',
  'capacity',
  'fuelType',
  'efficiencyRating',
  'refrigerantType',
  'filterSize',
  'expectedLifespanYears',
  'defaultManufacturerWarrantyMonths',
  'defaultManufacturerWarrantyTerms',
  'defaultServiceProviderWarrantyMonths',
  'defaultServiceProviderWarrantyTerms',
] as const satisfies readonly (keyof EquipmentTypeFormFields)[];

/** Reassemble a `form.watch(EQUIPMENT_SPEC_FIELD_NAMES)` tuple into the panels. */
export function equipmentSpecFieldsFromWatched(
  values: readonly unknown[],
): EquipmentTypeFormFields {
  const panels = Object.fromEntries(
    EQUIPMENT_SPEC_FIELD_NAMES.map((name, index) => [name, values[index]]),
  );
  // Identity is the ITEM's and the panels never state it — blank here so it can
  // never be mistaken for something the operator typed into them.
  return { name: '', code: '', ...panels } as EquipmentTypeFormFields;
}

/**
 * Exactly what the Equipment specifications + warranty panels show.
 *
 * Name, code and description are deliberately absent: they are the ITEM's
 * identity, and a type that was built on the Equipment Types page may carry its
 * own on purpose. A form only writes the fields it displays.
 */
export function equipmentTypeSpecFields(form: EquipmentTypeFormFields): EquipmentTypeSpec {
  return {
    category: form.equipmentCategoryKey,
    brand: form.brand || null,
    model: form.equipmentModel || null,
    manufacturer: form.manufacturer || null,
    mpn: form.mpn || null,
    upc: form.upc || null,
    lengthIn: form.lengthIn ?? null,
    widthIn: form.widthIn ?? null,
    heightIn: form.heightIn ?? null,
    weightLbs: form.weightLbs ?? null,
    capacity: form.capacity || null,
    fuelType: form.fuelType || null,
    efficiencyRating: form.efficiencyRating || null,
    refrigerantType: form.refrigerantType || null,
    filterSize: form.filterSize || null,
    expectedLifespanYears: form.expectedLifespanYears ?? null,
    defaultManufacturerWarrantyMonths: form.defaultManufacturerWarrantyMonths ?? null,
    defaultManufacturerWarrantyTerms: form.defaultManufacturerWarrantyTerms || null,
    defaultServiceProviderWarrantyMonths: form.defaultServiceProviderWarrantyMonths ?? null,
    defaultServiceProviderWarrantyTerms: form.defaultServiceProviderWarrantyTerms || null,
  };
}

/**
 * The same fields plus the identity a newly-born type takes from its item —
 * they are one thing under one code, not two catalogs to reconcile.
 */
export function newEquipmentTypeSpec(form: EquipmentTypeFormFields): EquipmentTypeSpec {
  return {
    ...equipmentTypeSpecFields(form),
    name: form.name,
    code: form.code,
    description: form.description?.trim() || null,
  };
}

/** Seed the spec panels from the type an existing item already links. */
export function equipmentTypeFormFields(
  type: EquipmentDescription,
): Required<Omit<EquipmentTypeFormFields, 'name' | 'code' | 'description'>> {
  return {
    equipmentCategoryKey: type.category,
    brand: type.brand ?? '',
    equipmentModel: type.model ?? '',
    manufacturer: type.manufacturer ?? '',
    mpn: type.mpn ?? '',
    upc: type.upc ?? '',
    lengthIn: type.lengthIn,
    widthIn: type.widthIn,
    heightIn: type.heightIn,
    weightLbs: type.weightLbs,
    capacity: type.capacity ?? '',
    fuelType: type.fuelType ?? '',
    efficiencyRating: type.efficiencyRating ?? '',
    refrigerantType: type.refrigerantType ?? '',
    filterSize: type.filterSize ?? '',
    expectedLifespanYears: type.expectedLifespanYears,
    defaultManufacturerWarrantyMonths: type.defaultManufacturerWarrantyMonths,
    defaultManufacturerWarrantyTerms: type.defaultManufacturerWarrantyTerms ?? '',
    defaultServiceProviderWarrantyMonths: type.defaultServiceProviderWarrantyMonths,
    defaultServiceProviderWarrantyTerms: type.defaultServiceProviderWarrantyTerms ?? '',
  };
}

/**
 * The spec panels are showing blanks that are not the truth.
 *
 * True while the linked type's request is in flight, and true for good if it
 * failed. Both the lock on the panels and the refusal to save them are asked
 * this same question, so they cannot drift apart and leave the panels
 * editable over a save that would ignore them.
 */
export function equipmentSpecPendingFor(
  linkedTypeId: string | null | undefined,
  loadedTypeId: string | null | undefined,
): boolean {
  return !!linkedTypeId && loadedTypeId !== linkedTypeId;
}

/** True when the operator has put anything at all in the spec panels. */
export function hasEquipmentSpecContent(form: EquipmentTypeFormFields): boolean {
  return Object.values(equipmentTypeSpecFields(form)).some(
    (value) => value !== null && value !== undefined && value !== '',
  );
}

/**
 * Resolve the equipment type an item links, creating it the first time.
 *
 * `carriedId` is a type an earlier attempt on this same form already created.
 * It exists because the type is written before the item save and that save can
 * still fail — a clashing item code is the everyday case. Creating the type
 * again on the retry would be rejected by the type's own code uniqueness (a
 * new type takes the item's code), leaving the operator unable to save at all,
 * and any spec they corrected in between would be lost. So a type this form
 * already made is updated in place and reused, never re-created.
 */
export async function ensureEquipmentType({
  carriedId,
  spec,
  create,
  update,
}: {
  carriedId: string | undefined;
  spec: EquipmentTypeSpec;
  create: (spec: EquipmentTypeSpec) => Promise<{ id?: string } | undefined>;
  update: (id: string, spec: EquipmentTypeSpec) => Promise<{ id?: string } | undefined>;
}): Promise<string> {
  if (carriedId) {
    await update(carriedId, spec);
    return carriedId;
  }
  const created = await create(spec);
  const id = created?.id;
  if (!id) throw new Error('the equipment type was saved without an id');
  return id;
}
